/**
 * Guest Request Status Notifications Hook
 * Phase P2.2-6: Guest Feedback on Request Progress
 *
 * Polls guest requests for the current stay and triggers notifications
 * when staff updates request status (NEW → IN_PROGRESS → COMPLETED).
 *
 * Features:
 * - Polls every 15 seconds
 * - Deduplication: Only notifies once per status change
 * - Guest matching: Only notifies current guest's requests
 * - Voice + Visual feedback
 * - Fullscreen-aware: Suppresses voice during TV fullscreen
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchGuestRequests, GuestRequestStatus } from '@/api';
import { zaxiaVoice } from '@/services';
import { useData } from '@/providers/DataProvider';

// =============================================================================
// TYPES
// =============================================================================

export interface RequestNotification {
  message: string;
  type: 'in_progress' | 'completed';
}

// Track which request+status combinations we've already notified about
type NotificationKey = `${string}-${GuestRequestStatus}`;

// =============================================================================
// HOOK
// =============================================================================

export function useGuestRequestNotifications(isTvFullscreen: boolean = false) {
  const { stay } = useData();
  const [notification, setNotification] = useState<RequestNotification | null>(null);
  
  // Track which statuses we've already notified about
  const notifiedStatusesRef = useRef<Set<NotificationKey>>(new Set());
  
  // Track last known status of each request
  const lastKnownStatusesRef = useRef<Map<string, GuestRequestStatus>>(new Map());

  // Clear notification
  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Trigger notification (voice + visual)
  const triggerNotification = useCallback((message: string, type: 'in_progress' | 'completed') => {
    // Visual notification (always)
    setNotification({ message, type });

    // Voice notification (only if not in fullscreen)
    if (!isTvFullscreen) {
      try {
        zaxiaVoice.speak(message, {
          onError: (error) => {
            console.error('[RequestNotifications] TTS error:', error);
          },
        });
      } catch (error) {
        console.error('[RequestNotifications] Failed to speak:', error);
      }
    }

    console.log('[RequestNotifications] Notification triggered:', { message, type, voiceSuppressed: isTvFullscreen });
  }, [isTvFullscreen]);

  // Poll guest requests
  useEffect(() => {
    if (!stay) {
      console.log('[RequestNotifications] No active stay - skipping polling');
      return;
    }

    let isActive = true;

    async function pollRequests() {
      if (!isActive || !stay) return;

      try {
        const allRequests = await fetchGuestRequests();
        
        // Filter requests for current stay only
        const myRequests = allRequests.filter(req => req.stayId === stay.stayId);

        // Check for status changes
        myRequests.forEach(request => {
          const lastStatus = lastKnownStatusesRef.current.get(request.id);
          const currentStatus = request.status;
          const notificationKey: NotificationKey = `${request.id}-${currentStatus}`;

          // Skip if we've already notified about this request+status combination
          if (notifiedStatusesRef.current.has(notificationKey)) {
            return;
          }

          // Skip if this is the first time we're seeing this request (no status change)
          if (!lastStatus) {
            lastKnownStatusesRef.current.set(request.id, currentStatus);
            notifiedStatusesRef.current.add(notificationKey);
            return;
          }

          // Status changed! Check if it's a transition we care about
          if (lastStatus === GuestRequestStatus.NEW && currentStatus === GuestRequestStatus.IN_PROGRESS) {
            triggerNotification('Someone is on the way.', 'in_progress');
            notifiedStatusesRef.current.add(notificationKey);
            lastKnownStatusesRef.current.set(request.id, currentStatus);
            console.log('[RequestNotifications] Status change detected:', {
              requestId: request.id,
              from: lastStatus,
              to: currentStatus,
            });
          }
          
          if (
            (lastStatus === GuestRequestStatus.IN_PROGRESS && currentStatus === GuestRequestStatus.COMPLETED) ||
            (lastStatus === GuestRequestStatus.NEW && currentStatus === GuestRequestStatus.COMPLETED)
          ) {
            triggerNotification('Your request has been taken care of.', 'completed');
            notifiedStatusesRef.current.add(notificationKey);
            lastKnownStatusesRef.current.set(request.id, currentStatus);
            console.log('[RequestNotifications] Status change detected:', {
              requestId: request.id,
              from: lastStatus,
              to: currentStatus,
            });
          }
        });
      } catch (error) {
        // Silent failure - don't disrupt guest experience
        console.error('[RequestNotifications] Polling error:', error);
      }
    }

    // Initial poll
    pollRequests();

    // Poll every 15 seconds
    const interval = setInterval(pollRequests, 15000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [stay, triggerNotification]);

  return {
    notification,
    dismissNotification,
  };
}
